import { normalizeUrl, urlIsExternal } from './url';

/**
 * Replaces all occurrences of `&amp;shy;` and `&amp;nbsp;` with their respective HTML entities.
 */
export function replaceHtmlEntities(text: string) {
  return text.replaceAll('&amp;shy;', '&shy;').replaceAll('&amp;nbsp;', '&nbsp;');
}

/**
 * Marks any HTMLAnchorElement that is a child of the given element and has a href value that is considered external.
 * It adds the class `is-external`, an inline icon and sets the target attribute to `_blank`.
 */
export function processExternalLinks(parent: HTMLElement, options: { preventInlineIcons?: boolean } = {}) {
  const iconSVG = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <line x1="4.65715" y1="19.0375" x2="18.7993" y2="4.89539" stroke="currentColor" stroke-width="2"/>
  <path d="M18.833 11.6633L18.833 4.92897L12.0987 4.92897" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
</svg>`;

  const links = [...parent.querySelectorAll('a')];
  links
    .filter(link => link.text.trim().length > 0)
    .forEach((link: HTMLAnchorElement) => {
      const href = link.getAttribute('href') ?? '';
      const target = link.getAttribute('target') ?? '';
      const isExternal = target === '_blank' || urlIsExternal(href);

      if (isExternal) {
        link.classList.add('is-external');
        link.setAttribute('target', '_blank');

        if (!options.preventInlineIcons) {
          link.innerHTML += iconSVG;
        }
      } else {
        // Normalize internal urls
        link.setAttribute('href', normalizeUrl(href));
      }
    });
}

/**
 * Removes any empty paragraphs from the given element.
 */
export function removeEmptyParagraphs(parent: HTMLElement) {
  parent.querySelectorAll('p').forEach(paragraph => {
    if (!paragraph.textContent || paragraph.textContent.trim() === '') {
      paragraph.remove();
    }
  });
}

type ItemCardCurrency = 'percentage' | 'euro' | 'dollar' | 'frank' | 'pound' | 'yen' | 'won' | 'rial';

const ITEM_CARD_CURRENCIES: Record<ItemCardCurrency, string> = {
  euro: '€',
  dollar: '$',
  frank: '₣',
  pound: '£',
  yen: '¥',
  won: '₩',
  rial: '﷼',
  percentage: '%',
};

export function formatItemCardContentNumbers({
  locale,
  frontSideNumber,
  frontSideNumberPrefix,
  frontSideNumberUnit,
}: {
  locale: string;
  frontSideNumber: string;
  frontSideNumberPrefix?: 'less-than' | 'greater-than' | '';
  frontSideNumberUnit?: ItemCardCurrency | '';
}) {
  const number = parseInt(frontSideNumber);
  let numberString = number.toString();

  function formatNumber(number: number) {
    return Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(number);
  }

  if (number >= 1_000_000_000) {
    // TODO should we use locale dependent big number suffixes here (e.g. "T" for thousands in "de_de"?
    const billions = number / 1_000_000_000;
    numberString = `${formatNumber(billions)}B`;
  } else if (number >= 1_000_000) {
    const millions = number / 1_000_000;
    numberString = `${formatNumber(millions)}M`;
  } else if (number >= 1_000) {
    const thousands = number / 1_000;
    numberString = `${formatNumber(thousands)}K`;
  }

  if (frontSideNumberPrefix) {
    switch (frontSideNumberPrefix) {
      case 'less-than':
        numberString = `< ${numberString}`;
        break;
      case 'greater-than':
        numberString = `> ${numberString}`;
        break;
      default:
        break;
    }
  }

  if (frontSideNumberUnit) {
    numberString += ` ${ITEM_CARD_CURRENCIES[frontSideNumberUnit]}`;
  }

  return numberString;
}
